import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useRestaurantScope } from "@/hooks/useRestaurantScope";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import EmptyState from "@/components/ms/EmptyState";
import Button from "@/components/ms/Button";
import { SkeletonCard } from "@/components/ms/Skeleton";
import { ArrowRight, CheckCircle2, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import i18n from "@/i18n";

const PRIORITY_LABEL = {
    critical: () => i18n.t("workflow.priority.critical", "Critical"),
    medium: () => i18n.t("workflow.priority.medium", "Medium"),
    low: () => i18n.t("workflow.priority.low", "Low"),
    completed: () => i18n.t("workflow.priority.completed", "Done"),
};

// task.source_module -> route
const ROUTE_FOR_MODULE = {
    deliveries: "/deliveries",
    daily_checks: "/daily-checks",
    self_monitoring: "/daily-checks",
    daily_performance: "/daily-performance",
    weekly_menu: "/weekly-menu",
    inventory: "/inventory",
    announcements: "/announcements",
    purchase_needs: "/weekly-menu",
    suppliers: "/suppliers",
    production_review: "/production-review",
    engine: "/workflow",
};

// Message 486 · Where available, open the source module for the
// specific date carried on the task. Self Monitoring and Daily
// Performance tasks link to the dated calendar view.
function routeFor(task) {
    const base = ROUTE_FOR_MODULE[task.source_module] || "/workflow";
    if (task.source_module === "self_monitoring" && task.source_id) {
        return `/daily-checks?date=${encodeURIComponent(task.source_id)}`;
    }
    if (task.source_module === "daily_performance" && task.due_date) {
        return `/daily-performance?date=${encodeURIComponent(task.due_date)}`;
    }
    return base;
}

// Engine-generated task text is localised only when the stored value still
// matches its English template exactly; anything else is shown as stored.
const ENGINE_TEXT_KEYS = new Set(["workflow.task.inventory_overdue.title", "workflow.task.inventory_overdue.description", "workflow.task.surface_test_overdue.title", "workflow.task.surface_test_overdue.description", "workflow.task.daily_temperature_missing.title", "workflow.task.daily_temperature_missing.description", "workflow.task.weekly_menu_incomplete.title", "workflow.task.weekly_menu_incomplete.description", "workflow.task.announcement_unread.title", "workflow.task.production_review_pending.title", "workflow.task.production_review_pending.description", "workflow.task.self_monitoring_overdue.title", "workflow.task.self_monitoring_overdue.description", "workflow.task.daily_performance_missing.title", "workflow.task.daily_performance_missing.description", "workflow.task.delivery_review_daily.title", "workflow.task.delivery_review_daily.description", "workflow.task.delivery_review.title", "workflow.task.delivery_review.description", "workflow.task.inventory.title", "workflow.task.inventory.description"]);
export function localizeTaskText(task, field) {
    const stored = task[field];
    const key = `workflow.task.${task.task_type}.${field}`;
    if (!stored || !ENGINE_TEXT_KEYS.has(key)) return stored;
    const template = i18n.getResource("en", "translation", key);
    if (typeof template !== "string") return stored;
    const names = [];
    const pattern = template.split(/(\{\{\w+\}\})/).map((part) => {
        const v = part.match(/^\{\{(\w+)\}\}$/);
        if (v) { names.push(v[1]); return "([\\s\\S]+?)"; }
        return part.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    }).join("");
    const hit = stored.match(new RegExp(`^${pattern}$`));
    if (!hit) return stored;
    const vars = Object.fromEntries(names.map((n, i) => [n, hit[i + 1]]));
    if (vars.type && i18n.language !== "en") {
        vars.type = i18n.t(`inventory.countType.${vars.type.toLowerCase().replace(/ /g, "_")}`, vars.type);
    }
    return i18n.t(key, { ...vars, defaultValue: stored, interpolation: { escapeValue: false } });
}

function actionLabelFor(task) {
    const t = i18n.t.bind(i18n);
    switch (task.source_module) {
        case "deliveries":
            return task.task_type === "delivery_review_daily"
                ? t("workflow.action.reviewDeliveries", "Review deliveries") : t("workflow.action.reviewDelivery", "Review delivery");
        case "weekly_menu": return t("workflow.action.openMenu", "Open menu");
        case "inventory":
            return task.task_type === "inventory_overdue" ? t("workflow.action.startInventory", "Start inventory") : t("workflow.action.openInventory", "Open inventory");
        case "daily_checks": return t("workflow.action.completeDailyChecks", "Complete daily checks");
        case "self_monitoring": return t("workflow.action.openSelfMonitoring", "Open Self Monitoring");
        case "daily_performance": return t("workflow.action.recordPerformance", "Record performance");
        case "announcements": return t("workflow.action.viewAnnouncement", "View announcement");
        case "purchase_needs": return t("workflow.action.openPurchaseNeeds", "Open purchase needs");
        case "production_review": return t("workflow.action.reviewProduction", "Review production");
        default: return t("workflow.action.open", "Open");
    }
}

function TaskCard({ task, onComplete }) {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const route = routeFor(task);
    const isCompleted = task.status === "completed" || task.priority === "completed";
    // Workflow Correction · Self Monitoring tasks follow the real
    // source records (temperature logs / daily checks). The chef must
    // complete them in Self Monitoring; the Workflow checkbox is
    // disabled here and the server also rejects the completion.
    const canManualComplete = task.task_type !== "self_monitoring_overdue";

    return (
        <div data-testid={`task-card-${task.id}`} className="kop-card p-5 flex flex-wrap sm:flex-nowrap items-start gap-4">
            <div className="mt-1 shrink-0">
                <StatusBadge status={isCompleted ? "completed" : (task.priority || "medium")}>
                    {PRIORITY_LABEL[isCompleted ? "completed" : task.priority]?.() || task.priority}
                </StatusBadge>
            </div>
            <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-neutral-900">{localizeTaskText(task, "title")}</div>
                {task.description && (
                    <div className="text-sm text-neutral-500 mt-1">{localizeTaskText(task, "description")}</div>
                )}
                <div className="text-[10px] text-neutral-400 mt-2 uppercase tracking-wide">
                    {t(`workflow.module.${task.source_module || "engine"}`, (task.source_module || "engine").replace(/_/g, " "))}
                    {task.due_date && (
                        <span className="ml-2">· {t("workflow.due", { defaultValue: "Due {{date}}", date: task.due_date })}</span>
                    )}
                </div>
            </div>
            <div data-testid={`task-actions-${task.id}`} className="flex w-full sm:w-auto items-center justify-end gap-2 shrink-0">
                {!isCompleted && canManualComplete && (
                    <button
                        data-testid={`task-complete-${task.id}`}
                        onClick={() => onComplete(task.id)}
                        title={t("workflow.markComplete", "Mark complete")}
                        className="h-10 w-10 grid place-items-center rounded-md border border-neutral-200 hover:bg-neutral-50"
                    >
                        <CheckCircle2 className="h-4 w-4 text-neutral-600" />
                    </button>
                )}
                <button
                    data-testid={`task-open-${task.id}`}
                    onClick={() => navigate(route)}
                    className="h-10 px-4 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-neutral-900 text-white text-sm font-semibold hover:bg-neutral-800"
                >
                    {actionLabelFor(task)} <ArrowRight className="h-4 w-4" />
                </button>
            </div>
        </div>
    );
}

// Launch Fix §1 — CorporateSummary (multi-restaurant grid) removed.
// Corporate now sees only the menu status for the currently selected
// restaurant, driven by the Restaurant selector in the top bar.

export default function Workflow() {
    const { user, currentRestaurant, restaurantId } = useApp();
    const { scopedParams } = useRestaurantScope();
    const [tasks, setTasks] = useState([]);
    const [showCompleted, setShowCompleted] = useState(false);
    const [loading, setLoading] = useState(true);
    const { t } = useTranslation();

    const load = useCallback(async () => {
        if (!restaurantId) return;
        setLoading(true);
        try {
            const { data } = await api.get("/workflow/tasks", { params: { ...scopedParams } });
            setTasks(data);
        } finally { setLoading(false); }
    }, [restaurantId, scopedParams]);

    useEffect(() => { load(); }, [load]);

    const completeTask = async (id) => {
        await api.post(`/workflow/tasks/${id}/complete`);
        load();
    };

    const isPending = (t) => t.status === "pending";
    const critical = tasks.filter((t) => isPending(t) && t.priority === "critical");
    const medium = tasks.filter((t) => isPending(t) && t.priority === "medium");
    const low = tasks.filter((t) => isPending(t) && t.priority === "low");
    const completed = tasks.filter((t) => t.status === "completed");
    const pendingTotal = critical.length + medium.length + low.length;

    return (
        <div className="px-6 lg:px-12 py-6 max-w-6xl mx-auto">
            <PageHeader
                title={t("workflow.title", "Workflow")}
                subtitle={user?.role === "corporate"
                    ? t("workflow.subtitleCorporate", { defaultValue: "Across {{name}} — what still needs to be done today.", name: currentRestaurant?.name || t("workflow.yourRestaurants", "your restaurants") })
                    : t("workflow.subtitle", "What still needs to be done today.")}
                testId="workflow-header"
            />

            {/* Notification banner */}
            {(critical.length + medium.length + low.length) > 0 && (
                <div className="kop-card p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="workflow-notification">
                    {critical.length > 0 && (
                        <StatusBadge status="critical" testId="badge-critical-count">
                            {t("workflow.criticalCount", { defaultValue: "{{count}} Critical", count: critical.length })}
                        </StatusBadge>
                    )}
                    {medium.length > 0 && (
                        <StatusBadge status="pending" testId="badge-medium-count">
                            {t("workflow.mediumCount", { defaultValue: "{{count}} Medium", count: medium.length })}
                        </StatusBadge>
                    )}
                    {low.length > 0 && (
                        <StatusBadge status="low" testId="badge-low-count">
                            {t("workflow.lowCount", { defaultValue: "{{count}} Low", count: low.length })}
                        </StatusBadge>
                    )}
                    <span className="text-sm text-neutral-500">{t("workflow.pointsOnlyToWorkflow", "— points only to Workflow.")}</span>
                </div>
            )}

            {/*
              Launch Fix §1 — Corporate Workflow shows the menu status of
              the currently selected restaurant only. The multi-restaurant
              grid was removed on purpose: the Restaurant selector drives
              this view. MenuStatusCards below already renders This Week /
              Next Week status (Not Started / Planning / Ready) plus the
              per-day breakdown for This Week.
            */}
            <MenuStatusCards restaurantId={restaurantId} />

            {!loading && (
                <>
                    <Section title={t("workflow.actionRequired", "Action Required")} count={critical.length} tone="critical" testId="section-action-required">
                        {critical.length === 0 ? (
                            <EmptySection text={t("workflow.nothingUrgent", "Nothing urgent.")} />
                        ) : (
                            critical.map((t) => <TaskCard key={t.id} task={t} onComplete={completeTask} />)
                        )}
                    </Section>

                    <Section title={t("workflow.pending", "Pending")} count={medium.length + low.length} tone="pending" testId="section-pending">
                        {medium.length + low.length === 0 ? (
                            <EmptySection text={t("workflow.nothingPending", "Nothing pending.")} />
                        ) : (
                            <>
                                {medium.map((t) => <TaskCard key={t.id} task={t} onComplete={completeTask} />)}
                                {low.map((t) => <TaskCard key={t.id} task={t} onComplete={completeTask} />)}
                            </>
                        )}
                    </Section>

                    <div className="mt-10">
                        <Button
                            variant="ghost"
                            size="sm"
                            testId="toggle-completed"
                            onClick={() => setShowCompleted((s) => !s)}
                        >
                            {showCompleted ? t("workflow.hideCompleted", "Hide completed") : t("workflow.showCompletedToday", { defaultValue: "Show completed today ({{count}})", count: completed.length })}
                        </Button>
                        {showCompleted && (
                            <div className="mt-4 space-y-3" data-testid="section-completed">
                                {completed.length === 0
                                    ? <EmptySection text={t("workflow.nothingCompletedToday", "Nothing completed today.")} />
                                    : completed.map((t) => <TaskCard key={t.id} task={t} onComplete={completeTask} />)}
                            </div>
                        )}
                    </div>
                </>
            )}

            {loading && (
                <div className="space-y-3 mt-6" data-testid="workflow-loading">
                    <SkeletonCard rows={2} />
                    <SkeletonCard rows={2} />
                    <SkeletonCard rows={2} />
                </div>
            )}

            {!loading && pendingTotal === 0 && completed.length === 0 && (
                <EmptyState
                    icon={Sparkles}
                    title={t("workflow.allSetTitle", "All set for today.")}
                    description={t("workflow.allSetDescription", "No pending kitchen tasks for this restaurant. Enjoy a calm service.")}
                    testId="workflow-empty"
                />
            )}
        </div>
    );
}


// Launch Audit R1 §1 — Menu Status Cards.
// Shows the current + next week planning status for the currently
// selected restaurant so the chef doesn't have to open Weekly Menu.
function MenuStatusCards({ restaurantId }) {
    const { t } = useTranslation();
    const [status, setStatus] = useState(null);
    useEffect(() => {
        if (!restaurantId) return;
        api.get("/weekly-menus/status", { params: { restaurant_id: restaurantId } })
            .then(({ data }) => setStatus(data))
            .catch(() => setStatus(null));
    }, [restaurantId]);
    if (!status) return null;
    const readyDays = (w) => w.days.filter((d) => d.status === "ready" || d.status === "closed").length;
    return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-testid="menu-status-cards">
            <WeekCard title={t("workflow.thisWeek", "This Week")} week={status.current_week} ready={readyDays(status.current_week)} testId="menu-status-current" />
            <WeekCard title={t("workflow.nextWeek", "Next Week")} week={status.next_week} ready={readyDays(status.next_week)} testId="menu-status-next" />
        </div>
    );
}

function WeekCard({ title, week, ready, testId }) {
    const { t } = useTranslation();
    const label = week.status === "ready" ? t("workflow.weekStatus.ready", "Ready")
                : week.status === "planning" ? t("workflow.weekStatus.planning", "Planning")
                : t("workflow.weekStatus.notStarted", "Not Started");
    const tone = week.status === "ready" ? "completed"
                : week.status === "planning" ? "pending"
                : "action_required";
    return (
        <div className="kop-card p-5" data-testid={testId}>
            <div className="flex items-start justify-between mb-3">
                <div>
                    <div className="text-xs uppercase tracking-wide text-neutral-500 font-semibold">{title}</div>
                    <div className="font-display text-xl font-bold mt-0.5">{label}</div>
                    <div className="text-xs text-neutral-500 mt-1">{t("workflow.daysReady", { defaultValue: "{{ready}} / 7 days ready · {{pct}}% complete", ready, pct: week.completion })}</div>
                </div>
                <StatusBadge status={tone}>{label}</StatusBadge>
            </div>
            <div className="grid grid-cols-7 gap-1" data-testid={`${testId}-days`}>
                {week.days.map((d) => (
                    <div key={d.date}
                        data-testid={`${testId}-day-${d.date}`}
                        data-status={d.status}
                        className={`rounded-md px-1.5 py-2 text-center text-[10px] font-semibold uppercase
                            ${d.status === "ready" ? "bg-emerald-100 text-emerald-800" :
                              d.status === "closed" ? "bg-neutral-200 text-neutral-700" :
                              d.status === "in_progress" ? "bg-amber-100 text-amber-800" :
                              "bg-neutral-100 text-neutral-400"}`}>
                        <div>{t(`daysShort.${(d.day_name || "").toLowerCase()}`, d.day_name.slice(0, 3))}</div>
                        <div className="text-[9px] mt-0.5 tracking-normal">
                            {d.status === "ready" && t("workflow.dayStatus.ready", "Ready")}
                            {d.status === "closed" && t("workflow.dayStatus.closed", "Closed")}
                            {d.status === "in_progress" && t("workflow.dayStatus.inProgress", "In prog")}
                            {d.status === "not_planned" && "—"}
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}


function Section({ title, count, tone, children, testId }) {
    return (
        <section className="mt-10" data-testid={testId}>
            <div className="flex items-baseline gap-3 mb-4">
                <h2 className="font-display text-xl font-bold">{title}</h2>
                <StatusBadge status={tone}>{count}</StatusBadge>
            </div>
            <div className="space-y-3">{children}</div>
        </section>
    );
}

function EmptySection({ text }) {
    return (
        <div className="kop-card p-6 text-sm text-neutral-500">{text}</div>
    );
}
